import React, { useContext, useEffect, useMemo, useState } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import styled from "styled-components";
import { ASSET_TYPE } from "./VehicleManagementConstants";
import { VEHICLE_DETAIL_COL, vehicleRowActions } from "./VehicleManagementGridConfig";
import NewGridComponent from "./NewGridComponent";
import { uniq } from "lodash";
import { PrivilegeContext } from "../../../context/privilegeContext";
import onDemand from "../../assets/icons/on_demand.svg";
import VehicleDetailTable from "./VehicleDetailTable";
import SubscriptionIcons from "./SubscriptionIcons";

export const AccordionStyle = styled.div`
  .p-accordion {
    margin-top: 10px;
    margin-bottom: 10px;
    padding: 0;
  }
  .p-accordion .p-accordion-header {
    width: 100%;
    background: #f8f3e9;
    box-shadow: none;
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    margin-top: 10px;
    padding-left: 0;
    transition: border-bottom 0.2s;
    borderradius: 8px;
  }
  .p-accordion .p-accordion-header.p-highlight {
    border-bottom: none !important;
    background: #ffffff;
    color: #000000;
    box-shadow: none;
  }
  .p-accordion .p-accordion-header:not(.p-disabled):hover,
  .p-accordion .p-accordion-header:not(.p-disabled):focus {
    background: #f8f3e9;
    color: #000000;
    box-shadow: none;
  }

  /* Header link should always fill full width (normal, hover, highlight) */
  .p-accordion .p-accordion-header .p-accordion-header-link,
  .p-accordion .p-accordion-header.p-highlight .p-accordion-header-link,
  .p-accordion .p-accordion-header:not(.p-disabled):hover .p-accordion-header-link {
    width: 100%;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    color: #000000;
    border: none;
    padding: 0.5rem 0; /* same padding in all states */
    box-sizing: border-box;
    border-radius: unset;
    background: #f8f3e9;
    margin: 0;
  }

  /* Move toggle icon to the right */
  .p-accordion .p-accordion-header .p-accordion-toggle-icon {
    margin-left: auto; /* push icon to the far right */
    order: 2;
    flex-shrink: 0;
  }

  /* Ensure header link lays out content and icon with space-between */
  .p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link,
  .p-accordion .p-accordion-tab .p-accordion-header:hover .p-accordion-header-link,
  .p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #000000;
    border: none;
    padding: 0.5rem 0;
    box-shadow: unset;
    box-color: #ffffff;
    border-radius: unset;
    background: #f8f3e9;
    margin: 0;
  }
  .p-accordion .p-accordion-content {
    padding: 0rem;
    border: none;
  }
  /* Allow text to truncate instead of forcing layout changes */
  .p-accordion .p-accordion-header .p-accordion-header-text {
    min-width: 0;
  }
  .p-accordion-header-text {
    font-weight: 700;
    font-family: Verizon-NHG-eDS;
  }
`;

const VehicleDetailsTableComponent = ({
  isBulkActionSelected,
  shippingAddress = [],
  vehicleDetails = [],
  selectedVehicles = [],
  setSelectedVehicles,
  selectedAction,
}) => {
  const [selectedVehicleIds, setSelectedVehicleIds] = useState(() => {
    // Initialize selectedVehicleIds with composite keys
    const initialSelectedVehicleIds = {};
    selectedVehicles.forEach((v) => {
      const compositeKey = `${v.addressId}-${v.contactId}`;
      if (!initialSelectedVehicleIds[compositeKey]) {
        initialSelectedVehicleIds[compositeKey] = [];
      }
      initialSelectedVehicleIds[compositeKey].push(v.id);
    });
    return initialSelectedVehicleIds;
  });

  const { cspInfo } = useContext(PrivilegeContext);
  const cspData = cspInfo.data.data;
  const products = cspData.Product || [];
  const rowExpansionTemplate = (row, gridTemplateColumns) => {
    return (
      <div>
        <VehicleDetailTable row={row} gridTemplateColumns={gridTemplateColumns} />
      </div>
    );
  };

  const bundleToIconCategoryMap = useMemo(() => {
    const uniqBundles = uniq(products.map((p) => p.BundleId));
    const map = {};
    uniqBundles.forEach((b) => {
      const list = uniq(products.filter((p) => p.BundleId === b).map((sub) => sub.iconCategory));
      map[b] = list;
    });
    return map;
  }, [products]);

  const formatShippingAddress = useMemo(() => {
    return shippingAddress.map((addr) => {
      const { address: { addressId, address, contactId, contactName, country, stateCode, city, zipCode } = {}, addressContactKey } = addr;
      return {
        addressId,
        contactId,
        contactName,
        address: `${address}, ${city}, ${stateCode} ${zipCode}, ${country}`,
        addressContactKey,
      };
    });
  }, [shippingAddress]);

  const formattedVehicleDetails = useMemo(() => {
    const productMap = products.reduce((map, product) => {
      map[product.BundleId] = product;
      return map;
    }, {});

    const assetTypeOrder = {
      [ASSET_TYPE.VEHICLE]: 1,
      [ASSET_TYPE.POWERED_ASSET]: 2,
      [ASSET_TYPE.NON_POWERED_ASSET]: 3,
    };

    return vehicleDetails.map((group) => {
      const { bundles, addressId, contactId } = group;

      const sortedBundles = [...bundles].sort((a, b) => {
        const typeA = productMap[a.bundleId] || "";
        const typeB = productMap[b.bundleId] || "";
        const assetTypeA = typeA?.AssetType !== ASSET_TYPE.VEHICLE ? typeA?.iconCategory : typeA?.AssetType || "";
        const assetTypeB = typeB?.AssetType !== ASSET_TYPE.VEHICLE ? typeB?.iconCategory : typeB?.AssetType || "";
        return (assetTypeOrder[assetTypeA] || 3) - (assetTypeOrder[assetTypeB] || 3);
      });

      const rowData = sortedBundles.reduce((acc, cur) => {
        const { bundleId, vehicles } = cur;
        const product = productMap[bundleId];
        if (product) {
          const { BundleName, AssetType, iconCategory } = products.find((p) => p.BundleId === bundleId) || {};
          acc.push({
            isFullRow: true,
            bundleId: bundleId,
            bundleName: BundleName,
            assetType: AssetType,
            subscriptions: bundleToIconCategoryMap[bundleId],
            iconCategory: iconCategory,
          });
          acc.push(
            ...vehicles.map((e) => ({
              ...e,
              addressId,
              contactId,
              bundleId,
              bundleName: BundleName,
              assetType: AssetType,
              addressContactKey: `${addressId}-${contactId}`,
              iconCategory: iconCategory,
              subscriptions: bundleToIconCategoryMap[bundleId],
            }))
          );
        }
        return acc;
      }, []);
      return {
        addressId,
        contactId,
        tableData: rowData,
        addressContactKey: `${addressId}-${contactId}`,
      };
    });
  }, [vehicleDetails, bundleToIconCategoryMap, products]);

  // When bulk action bar is closed, clear selection state to reset the selected count.
  useEffect(() => {
    if (!isBulkActionSelected) {
      setSelectedVehicleIds({});
      setSelectedVehicles([]);
    }
  }, [isBulkActionSelected, setSelectedVehicles]);

  // Clear selection when bulk bar is opened (Start a bulk action)
  useEffect(() => {
    if (isBulkActionSelected) {
      setSelectedVehicleIds({});
    }
  }, [isBulkActionSelected]);

  useEffect(() => {
    // When selectedAction changes (i.e. an action is changed), clear selection state to reset the selected count.
    if (!selectedAction) return;
    setSelectedVehicleIds({});
    setSelectedVehicles([]);
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith("NewGridComponent:selected:"))
      .forEach((key) => window.localStorage.removeItem(key));
  }, [selectedAction, setSelectedVehicles]);

  return (
    <>
      <AccordionStyle>
        <Accordion
          expandIcon="pi pi-chevron-down"
          collapseIcon="pi pi-chevron-up"
          activeIndex={formatShippingAddress.findIndex(
            (shippingInfo) => formattedVehicleDetails.find((v) => v.addressContactKey === shippingInfo.addressContactKey)?.tableData?.length > 0
          )}
        >
          {formatShippingAddress.map((shippingInfo, index) => {
            const selectedCount = selectedVehicleIds[shippingInfo.addressContactKey]?.length || 0;
            const tableData = formattedVehicleDetails.find((v) => v.addressContactKey === shippingInfo.addressContactKey)?.tableData || [];
            if (tableData.length !== 0) {
              return (
                <AccordionTab
                  key={index}
                  headerTemplate={() => (
                    <div style={{ display: "flex", padding: "0.75rem", width: "100%" }}>
                      <div style={{ marginTop: "10px" }}>
                        <img src={onDemand} alt="ondemand-icon" width={20} height={20} />
                      </div>
                      <div style={{ paddingLeft: "1rem" }}>
                        <b>SHIPPING ADDRESS</b>
                        <div>
                          {shippingInfo.address} | <b>Contact:</b> {shippingInfo.contactName}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: selectedCount === 0 ? "#6f7171" : "#000",
                          fontWeight: 700,
                          opacity: selectedCount === 0 ? 0.6 : 1,
                          marginLeft: "auto",
                          marginTop: "12px",
                        }}
                        aria-disabled={selectedCount === 0}
                      >
                        Selected: {selectedCount}
                      </div>
                    </div>
                  )}
                >
                  <div style={{ overflowX: "auto" }}>
                    <NewGridComponent
                      gridId={`vehicle-grid:${shippingInfo.addressContactKey}`}
                      rows={tableData}
                      columns={VEHICLE_DETAIL_COL}
                      isSelectionEnabled={!!isBulkActionSelected}
                      enableRowExpansion
                      rowExpansionTemplate={rowExpansionTemplate}
                      rowActions={vehicleRowActions}
                      selectedRows={selectedVehicleIds[shippingInfo.addressContactKey] || []} // Pass selected IDs for the current shipping group
                      onSelectionChange={(selectedIds) => {
                        // Update the selectedVehicleIds state for the current shipping group
                        setSelectedVehicleIds((prev) => {
                          const updated = {
                            ...prev,
                            [shippingInfo.addressContactKey]: selectedIds, // Update the selected IDs for the current address
                          };

                          // Update the selectedVehicles array to reflect all selected vehicles across groups
                          const allSelectedVehicles = Object.values(updated).flat();
                          setSelectedVehicles(allSelectedVehicles);

                          return updated;
                        });
                      }}
                      renderFullRow={(row) => (
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            background: "#DDDAD4",
                            padding: 12,
                          }}
                        >
                          <div>
                            <span style={{ fontWeight: 700 }}>Bundle</span> | {row.bundleName} ({row.bundleId}) |
                            <span style={{ fontWeight: 700 }}>
                              {" "}
                              Subscriptions <SubscriptionIcons subscriptions={row.subscriptions} className="subscriptionIconsWrap--mleft" />
                            </span>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </AccordionTab>
              );
            }
          })}
        </Accordion>
      </AccordionStyle>
    </>
  );
};

export default VehicleDetailsTableComponent;
